import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  PhoneIcon, 
  Bars3Icon, 
  XMarkIcon 
} from '@heroicons/react/24/outline'
import logo from '../../images/web-logo.jpeg'

const contactInfo = {
  office: '703-373-2564',
  fax: '703-373-2565',
  mobile: '540-615-2418',
  email: 'aplusavadrivingschool@gmail.com',
}
//10 hr 499
const navLinks = [
  { href: '/', label: 'Home' },
  // { href: '/courses', label: 'Courses' },
  { href: '/reviews', label: 'Reviews' },
  { href: '/faq', label: 'FAQ' },
  { href: '/contact', label: 'Contact' },
]

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const pathname = location.pathname

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary text-white py-2 text-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
              <a href={`tel:${contactInfo.office}`} className="flex items-center gap-1 hover:text-accent transition-colors">
                <PhoneIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Office: </span>
                <span>{contactInfo.office}</span>
              </a>
              <span className="hidden md:inline">Fax: {contactInfo.fax}</span>
              <a href={`tel:${contactInfo.mobile}`} className="flex items-center gap-1 hover:text-accent transition-colors">
                <PhoneIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Mobile: </span>
                <span>{contactInfo.mobile}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="hover:text-accent transition-colors hidden lg:inline">
                {contactInfo.email}
              </a>
            </div>
            <a
              href={`tel:${contactInfo.mobile}`}
              className="bg-accent text-primary px-3 sm:px-4 py-1.5 rounded-full font-heading font-semibold hover:scale-105 transition-transform text-xs sm:text-sm"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header
        className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
          isScrolled ? 'shadow-soft' : ''
        }`}
      >
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
              <img 
                src={logo} 
                alt="A Plus A Driving School Logo" 
                className="w-12 h-12 sm:w-16 sm:h-16 object-contain rounded-full group-hover:scale-105 transition-transform"
              />
              <div>
                <div className="font-heading font-bold text-primary text-lg sm:text-xl">A Plus A</div>
                <div className="font-heading text-xs sm:text-sm text-secondary-text">Driving School</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-heading font-semibold transition-colors relative ${
                    pathname === link.href
                      ? 'text-primary'
                      : 'text-secondary-text hover:text-primary'
                  }`}
                >
                  {link.label}
                  {pathname === link.href && (
                    <motion.div
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent"
                      layoutId="activeTab"
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              {/* <Link
                to="/pay-online"
                className="px-6 py-2 border-2 border-primary text-primary font-heading font-semibold rounded-full hover:bg-primary hover:text-white transition-colors"
              >
                Pay Online
              </Link> */}
              <Link
                to="/register"
                className="px-6 py-2 bg-accent text-primary font-heading font-semibold rounded-full hover:scale-105 hover:shadow-lift transition-all"
              >
                Register Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-primary"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {/* @ts-ignore - framer-motion AnimatePresence type issue */}
        <AnimatePresence mode="wait" initial={false}>
          {isMobileMenuOpen && (
            <motion.div
              key="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-divider"
            >
              <div className="container mx-auto px-4 py-4 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-2 font-heading font-semibold ${
                      pathname === link.href
                        ? 'text-primary'
                        : 'text-secondary-text'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 space-y-2 border-t border-divider">
                  <Link
                    to="/pay-online"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-center px-6 py-2 border-2 border-primary text-primary font-heading font-semibold rounded-full"
                  >
                    Pay Online
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-center px-6 py-2 bg-accent text-primary font-heading font-semibold rounded-full"
                  >
                    Register Now
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  )
}

